import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiPost } from '@/api/client';
import { ConflictError } from '@/api/errors';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Field, FormActions, Input, Select } from '@/app/components/ui/Field';
import { Modal } from '@/app/components/ui/Modal';
import { useSubmit } from '@/lib/useSubmit';

interface CreatedPerson {
  user_id?: string;
  has_login?: boolean;
  invite_sent?: boolean;
  warning?: string;
}

/**
 * The Medewerkers list's "Uitnodigen" modal (PLAN-FASE2.md WP-01): Naam,
 * E-mailadres, Rol. Hergebruikt de bestaande invite-flow — `POST /users`
 * met `company_id` en een e-mailadres is exact wat `AddPersonPage.tsx` doet,
 * hier alleen als modal in plaats van een eigen route. "Rol" kiest tussen
 * lid (geen rolgrant) en company_admin (`POST /companies/{id}/roles` na
 * aanmaken), default Medewerker (geen rol).
 */
export function InviteUserModal({
  open,
  onClose,
  companyId,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onInvited: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'company_admin'>('member');
  const [result, setResult] = useState<CreatedPerson | null>(null);

  const form = useSubmit(async () => {
    const created = (await apiPost('/users', {
      body: {
        company_id: companyId,
        ...(name.trim() !== '' ? { name } : {}),
        ...(email.trim() !== '' ? { email } : {}),
      },
    })) as CreatedPerson | undefined;

    if (role === 'company_admin' && created?.user_id) {
      await apiPost('/companies/{companyId}/roles', {
        params: { companyId },
        body: { user_id: created.user_id },
      });
    }

    setResult(created ?? null);
    onInvited();
  });

  function reset() {
    setName('');
    setEmail('');
    setRole('member');
    setResult(null);
    form.clearError();
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Modal open={open} onClose={handleClose} title={t('users.invite.title')}>
      {result ? (
        <div role="status" className="flex flex-col gap-4">
          <p className="text-ink text-sm">
            {t('users.addPerson.created')}{' '}
            {result.invite_sent
              ? t('users.addPerson.inviteSent')
              : result.has_login
                ? t('users.addPerson.alreadyHasLogin')
                : t('users.addPerson.noLoginYet')}
          </p>
          {result.warning && <p className="text-warning text-[13px]">{result.warning}</p>}
          <FormActions>
            <Button onClick={handleClose}>{t('users.addPerson.done')}</Button>
          </FormActions>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
        >
          <Field label={t('users.invite.name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label={t('users.invite.email')}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label={t('users.invite.role')}>
            <Select
              value={role}
              onChange={(e) =>
                setRole(e.target.value === 'company_admin' ? 'company_admin' : 'member')
              }
            >
              <option value="member">{t('users.invite.roleMember')}</option>
              <option value="company_admin">{t('users.invite.roleAdmin')}</option>
            </Select>
          </Field>
          {form.error instanceof ConflictError ? (
            <Alert tone="danger">
              {t('users.addPerson.conflict', {
                reason: form.error.reason ? ` (${form.error.reason})` : '',
              })}
            </Alert>
          ) : (
            <SubmitError error={form.error} />
          )}
          <FormActions>
            <Button type="button" variant="ghost" onClick={handleClose}>
              {t('users.invite.cancel')}
            </Button>
            <Button type="submit" disabled={form.pending || email.trim() === ''}>
              {t('users.invite.submit')}
            </Button>
          </FormActions>
        </form>
      )}
    </Modal>
  );
}
